import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING, ADD, EDIT } from "const"
import { OkrSession } from "server/models"
import { StateType, hasAccess } from "client/utils"
import {
    PRM_OKR_SESSION,
    StoreType,
    OkrSessionProps,
    AccountUserList,

    LoadAccountUsers,
    LoadAllOkrSessions,
    LoadOkrSessionById,
    LoadAllOkrObjectives,
    LoadAllOkrKeyResults,
    SortOkrSessions,
    PrepareOkrSessionNestedIds,

    setAccountUserList,
    setOkrSessionAvailableSessions,
    setOkrSessionEdit,
    setOkrSessionView,
    setOkrObjectiveList,
    setOkrKeyResultList,
    setOkrSessionReloadEdit,
    initOkrSession,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
    TableNestedIds,
} from "client/components"

import NotFound from "client/parts/notFound"
import AccessDenied from "client/parts/accessDenied"
import Form from "./form"

const Edit = ({
    sessionId = null,
}: OkrSessionProps) => {
    const session = useSelector((state: StoreType) => state.okrSession.sessionEdit)
    const reloadEdit = useSelector((state: StoreType) => state.okrSession.reloadEdit)

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    const reload = () => {
        dispatch(setOkrSessionReloadEdit(false))
        setTimeout(() => {
            dispatch(setOkrSessionReloadEdit(true))
        }, 0)
    }

    useEffect(() => {
        const promiseArray: Promise<any>[] = [
            // account user list to set owner
            LoadAccountUsers(userData.accountId).then((res) => {
                const accountUserList: AccountUserList = { [userData.accountId]: res }
                dispatch(setAccountUserList(accountUserList))
            }),
            LoadAllOkrSessions(userData.accountId).then((sessionList) => {
                const sessionIds = PrepareOkrSessionNestedIds(Object.values(sessionList))
                const allowedSessionIds: number[] = []

                const addSessionIds = (nestedIds: readonly TableNestedIds[]) => {
                    nestedIds.forEach((nested) => {
                        const nestedId = Number(nested.id)
                        if (nestedId === sessionId) return // separate nested sessions and the current one
                        allowedSessionIds.push(nestedId)
                        addSessionIds(nested.nestedIds)
                    })
                }
                if (sessionIds[0]) addSessionIds(sessionIds[0].nestedIds) // where sessionIds[0] - top level group

                // show opened and in progress
                const sessionsFiltered = SortOkrSessions(Object.values(sessionList), {
                    showInProgress: true,
                    showArchived: false,
                })
                const displayedSessions: OkrSession[] = []
                sessionsFiltered.forEach((row) => {
                    if (allowedSessionIds.includes(row.id)) displayedSessions.push(row)
                })
                dispatch(setOkrSessionAvailableSessions(displayedSessions))
            }),
        ]

        if (sessionId === 0) {
            dispatch(setOkrSessionEdit(initOkrSession))
        } else {
            promiseArray.push(
                LoadOkrSessionById(sessionId, userData.accountId).then((res) => {
                    dispatch(setOkrSessionEdit(res))
                    // for editing key results in case of deadline conflicts
                    dispatch(setOkrSessionView(res))
                }),
                // for editing key results in case of deadline conflicts
                LoadAllOkrObjectives(sessionId).then((res) => {
                    dispatch(setOkrObjectiveList(res.objectiveList))
                }),
                LoadAllOkrKeyResults(sessionId).then((res) => {
                    dispatch(setOkrKeyResultList(res.keyResultList))
                }),
            )
        }

        setLoadState(PENDING)
        Promise.all(promiseArray).then(() => {
            setLoadState(IDLE)
            reload()
        })
    }, [])

    const addAccess = hasAccess(PRM_OKR_SESSION, ADD)
    const editAccess = hasAccess(PRM_OKR_SESSION, EDIT)

    const isOwner = session && session.owner === userData.userId
    const allowedToEdit = editAccess || isOwner

    if (!session) return <NotFound />
    if (sessionId === 0 && !addAccess) return <AccessDenied />
    if (sessionId !== 0 && !allowedToEdit) return <AccessDenied />

    return (
        <Container>
            <PageHeader>
                <PageTitle>
                    {session.id ? lang.editSession : lang.addSession}
                </PageTitle>
            </PageHeader>
            <PageContent>
                {loadState === PENDING && <Loader />}
                {
                    !reloadEdit
                    && <Form session={session} />
                }
                {
                    reloadEdit
                    && <Form session={session} />
                }
            </PageContent>
        </Container>
    )
}
export default Edit
